<!--
  The announcement popup itself: the frame around the pasted HTML - a blurred backdrop, the card,
  the X, and a footer with the action button (set in the editor) and the dismiss button. One
  component for every place an announcement is seen: the customer's popup (AnnouncementPopup), the
  root admin's full-screen preview, and - `inline` - the editor's live preview and the list's
  thumbnails, where it is just the card in the page.

  It only reports what the user did (dismiss / cta / close / action / navigate / link); what that
  means (tracking, following the button) is the owner's business.
-->
<template>

    <div :class="inline ? 'puAnnInline' : ['puAnnOverlay', { leaving: leaving }]"
         :role="inline ? null : 'dialog'"
         :aria-modal="inline ? null : 'true'"
         :aria-label="announcement.title || 'הודעה'"
         @mousedown="onMousedown"
         @click.self="onBackdropClick"
         @wheel="onWheel">

        <div class="puAnnCard"
             :class="'size-' + display.size"
             :style="cardStyle">

            <!-- the root admin inside an account sees what the customer gets - and that it doesn't count -->
            <div v-if="adminPreview && !inline"
                 class="puAnnAdminNote">
                תצוגה של מנהל המערכת · לא נספרת בנתונים
            </div>

            <button type="button"
                    class="puAnnClose"
                    aria-label="סגירה"
                    title="סגירה"
                    @click="$emit('close')">
                <svg viewBox="0 0 24 24"
                     width="16"
                     height="16"
                     fill="none"
                     stroke="currentColor"
                     stroke-width="2.4"
                     stroke-linecap="round"
                     aria-hidden="true">
                    <path d="M18 6L6 18M6 6l12 12" />
                </svg>
            </button>

            <!-- a fade at the bottom while there is more to scroll to -->
            <div class="puAnnBodyWrap"
                 :class="{ more: moreBelow }">
                <div ref="body"
                     class="puAnnBody"
                     @scroll="updateMoreBelow">
                    <AnnouncementHtml :html="announcement.html"
                                      :padded="display.padded"
                                      @action="name => $emit('action', name)"
                                      @navigate="path => $emit('navigate', path)"
                                      @link="href => $emit('link', href)" />
                </div>
            </div>

            <div class="puAnnFooter">

                <div class="puAnnButtons">

                    <button v-if="hasCta"
                            ref="primary"
                            type="button"
                            class="puAnnCta"
                            @click="$emit('cta')">
                        {{ display.ctaLabel }}
                        <svg viewBox="0 0 24 24"
                             width="16"
                             height="16"
                             fill="none"
                             stroke="currentColor"
                             stroke-width="2.4"
                             stroke-linecap="round"
                             stroke-linejoin="round"
                             aria-hidden="true">
                            <path d="M19 12H5M11 18l-6-6 6-6" />
                        </svg>
                    </button>

                    <button ref="dismiss"
                            type="button"
                            class="puAnnDismiss"
                            :class="{ primary: !hasCta }"
                            @click="$emit('dismiss')">{{ display.dismissLabel || 'הבנתי' }}</button>

                </div>

                <div v-if="total > 1"
                     class="puAnnDots"
                     :title="(index + 1) + ' מתוך ' + total">
                    <span v-for="n in total"
                          :key="n"
                          :class="{ on: n - 1 == index }"></span>
                </div>

            </div>

        </div>

    </div>

</template>

<script>

import AnnouncementHtml from './AnnouncementHtml.vue';

const WIDTHS = { sm: 440, md: 600, lg: 780 };

const GRACE_MS = 800;

function rgba(hex, alpha) {

    let match = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex || '');

    if (!match)
        return `rgba(125, 95, 196, ${alpha})`;

    return `rgba(${parseInt(match[1], 16)}, ${parseInt(match[2], 16)}, ${parseInt(match[3], 16)}, ${alpha})`;

}

export default {

    name: 'AnnouncementCard',

    components: { AnnouncementHtml },

    props: {
        announcement: { type: Object, required: true },
        inline: { type: Boolean, default: false },
        index: { type: Number, default: 0 },
        total: { type: Number, default: 1 },
        leaving: { type: Boolean, default: false },
        adminPreview: { type: Boolean, default: false },
    },

    data() {
        return { moreBelow: false, openedAt: Date.now(), downOnBackdrop: false };
    },

    computed: {

        display() {
            return Object.assign({ size: 'md', padded: true, ctaType: 'none', ctaLabel: '', dismissLabel: 'הבנתי', accent: '#7d5fc4' }, this.announcement.display || {});
        },

        hasCta() {
            return this.display.ctaType && this.display.ctaType != 'none' && !!this.display.ctaLabel;
        },

        cardStyle() {
            return {
                '--pu-width': (WIDTHS[this.display.size] || WIDTHS.md) + 'px',
                '--pu-accent': this.display.accent || '#7d5fc4',
                '--pu-accent-shadow': rgba(this.display.accent, 0.6),
                '--pu-accent-soft': rgba(this.display.accent, 0.12),
            };
        },

    },

    mounted() {

        if (this.inline)
            return;

        document.addEventListener('keydown', this.onKeydown);

        //keyboard users land on the main button; the page behind must not jump
        let button = this.$refs.primary || this.$refs.dismiss;
        if (button)
            button.focus({ preventScroll: true });

        //the content settles after its pictures and fonts arrive - the fade follows
        this.updateMoreBelow();
        if (window.ResizeObserver) {
            this.resizeObserver = new ResizeObserver(() => this.updateMoreBelow());
            this.resizeObserver.observe(this.$refs.body);
            if (this.$refs.body.firstElementChild)
                this.resizeObserver.observe(this.$refs.body.firstElementChild);
        }

    },

    beforeDestroy() {
        document.removeEventListener('keydown', this.onKeydown);
        if (this.resizeObserver)
            this.resizeObserver.disconnect();
    },

    methods: {

        updateMoreBelow() {
            let body = this.$refs.body;
            this.moreBelow = !this.inline && !!body && body.scrollHeight - body.scrollTop - body.clientHeight > 8;
        },

        //the first moment after it appears belongs to whatever the user was already doing: an
        //Escape or a click that was on its way does not close it
        settled() {
            return Date.now() - this.openedAt > GRACE_MS;
        },

        onKeydown(event) {
            if ((event.key === 'Escape' || event.keyCode === 27) && this.settled())
                this.$emit('close');
        },

        //a click outside closes only when it also started outside (not a drag that ended there)
        onMousedown(event) {
            this.downOnBackdrop = event.target === this.$el;
        },

        onBackdropClick() {

            if (!this.inline && this.downOnBackdrop && this.settled())
                this.$emit('close');

            this.downOnBackdrop = false;

        },

        //the page behind stays put: a wheel over the backdrop or the frame is swallowed, only the
        //content scrolls (and doesn't hand the scroll on to the page - overscroll-behavior)
        onWheel(event) {

            if (this.inline)
                return;

            let body = this.$el.querySelector('.puAnnBody');

            if (!body || !body.contains(event.target) || body.scrollHeight <= body.clientHeight)
                event.preventDefault();

        },

    },

}

</script>

<style>
.puAnnOverlay {
    position: fixed;
    inset: 0;
    z-index: 19000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    direction: rtl;
    background: rgba(24, 14, 52, .5);
    -webkit-backdrop-filter: blur(6px) saturate(115%);
    backdrop-filter: blur(6px) saturate(115%);
    animation: puAnnFade .35s ease both;
}

.puAnnOverlay.leaving {
    animation: puAnnFadeOut .22s ease both;
}

.puAnnInline {
    direction: rtl;
}

.puAnnCard {
    position: relative;
    width: 100%;
    max-width: var(--pu-width);
    max-height: calc(100vh - 48px);
    display: flex;
    flex-direction: column;
    background: #fff;
    border-radius: 24px;
    overflow: hidden;
    text-align: right;
    box-shadow: 0 40px 90px -28px rgba(42, 19, 110, .6), 0 14px 30px -14px rgba(0, 0, 0, .28);
    animation: puAnnIn .6s cubic-bezier(.16, 1, .3, 1) both;
}

.puAnnOverlay.leaving .puAnnCard {
    animation: puAnnOut .22s ease both;
}

.puAnnAdminNote {
    position: absolute;
    top: 14px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 3;
    white-space: nowrap;
    padding: 4px 12px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    color: #fff;
    background: rgba(20, 12, 44, .72);
    -webkit-backdrop-filter: blur(6px);
    backdrop-filter: blur(6px);
    pointer-events: none;
}

.puAnnInline .puAnnCard {
    max-height: none;
    animation: none;
    box-shadow: 0 24px 60px -26px rgba(42, 19, 110, .5), 0 0 0 1px rgba(30, 16, 80, .07);
}

.puAnnBodyWrap {
    position: relative;
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

.puAnnBodyWrap::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 64px;
    pointer-events: none;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0), #fff 90%);
    opacity: 0;
    transition: opacity .2s ease;
}

.puAnnBodyWrap.more::after {
    opacity: 1;
}

.puAnnBody {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
}

/* the app styles every svg { width: 100% } - these are icons */
.puAnnClose svg,
.puAnnCta svg {
    flex: none;
    width: 16px;
    height: 16px;
}

.puAnnInline .puAnnBody {
    overflow: visible;
}

.puAnnClose {
    position: absolute;
    top: 14px;
    left: 14px;
    z-index: 2;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    border: 0;
    padding: 0;
    display: grid;
    place-items: center;
    color: #3a3350;
    background: rgba(255, 255, 255, .78);
    -webkit-backdrop-filter: blur(6px);
    backdrop-filter: blur(6px);
    box-shadow: 0 2px 10px rgba(20, 10, 50, .16);
    cursor: pointer;
    transition: transform .25s ease, background .2s ease;
}

.puAnnClose:hover {
    background: #fff;
    transform: rotate(90deg);
}

.puAnnClose:focus-visible,
.puAnnCta:focus-visible,
.puAnnDismiss:focus-visible {
    outline: 3px solid var(--pu-accent-soft);
    outline-offset: 2px;
}

.puAnnFooter {
    flex: none;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 24px 20px;
    background: #fff;
}

.puAnnButtons {
    display: flex;
    align-items: center;
    gap: 8px;
}

.puAnnCta {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
    border: 0;
    border-radius: 999px;
    padding: 11px 22px 11px 18px;
    font-size: 15px;
    font-weight: 700;
    color: #fff;
    background: var(--pu-accent);
    box-shadow: 0 12px 24px -12px var(--pu-accent-shadow);
    cursor: pointer;
    transition: transform .15s ease, filter .15s ease, box-shadow .15s ease;
}

.puAnnCta:hover {
    transform: translateY(-1px);
    filter: brightness(1.07);
    box-shadow: 0 16px 28px -12px var(--pu-accent-shadow);
}

.puAnnDismiss {
    white-space: nowrap;
    border: 0;
    border-radius: 999px;
    padding: 10px 16px;
    font-size: 14.5px;
    font-weight: 600;
    color: #6b7185;
    background: transparent;
    cursor: pointer;
    transition: background .15s ease, color .15s ease;
}

.puAnnDismiss:hover {
    color: #2a3142;
    background: #f3f2f8;
}

.puAnnDismiss.primary {
    padding: 11px 28px;
    font-weight: 700;
    color: #fff;
    background: var(--pu-accent);
    box-shadow: 0 12px 24px -12px var(--pu-accent-shadow);
}

.puAnnDismiss.primary:hover {
    filter: brightness(1.07);
}

.puAnnDots {
    margin-right: auto;
    display: flex;
    align-items: center;
    gap: 6px;
}

.puAnnDots span {
    width: 7px;
    height: 7px;
    border-radius: 99px;
    background: #ddd7ef;
    transition: width .25s ease, background .25s ease;
}

.puAnnDots span.on {
    width: 20px;
    background: var(--pu-accent);
}

@keyframes puAnnIn {
    from {
        opacity: 0;
        transform: translateY(28px) scale(.96);
    }

    to {
        opacity: 1;
        transform: none;
    }
}

@keyframes puAnnOut {
    to {
        opacity: 0;
        transform: translateY(14px) scale(.98);
    }
}

@keyframes puAnnFade {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

@keyframes puAnnFadeOut {
    to {
        opacity: 0;
    }
}

@keyframes puAnnSheetIn {
    from {
        transform: translateY(100%);
    }

    to {
        transform: none;
    }
}

/* phones: a bottom sheet */
@media (max-width: 576px) {

    .puAnnOverlay {
        align-items: flex-end;
        padding: 0;
    }

    .puAnnOverlay .puAnnCard {
        max-width: 100%;
        max-height: 92vh;
        border-radius: 22px 22px 0 0;
        animation: puAnnSheetIn .45s cubic-bezier(.16, 1, .3, 1) both;
    }

    .puAnnOverlay .puAnnFooter {
        padding: 12px 16px calc(16px + env(safe-area-inset-bottom));
    }

    .puAnnOverlay .puAnnButtons {
        flex: 1;
    }

    .puAnnOverlay .puAnnCta,
    .puAnnOverlay .puAnnDismiss.primary {
        flex: 1;
        justify-content: center;
    }

}

@media (prefers-reduced-motion: reduce) {

    .puAnnOverlay,
    .puAnnOverlay .puAnnCard {
        animation: none !important;
    }

}
</style>
